import type { AppResource, ExperimentResource, StoreDesignResource } from '@studio/shared';
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { ArchivedApps, isInWork } from '../app/Archive.js';
import { useActivity } from '../shell/activity/index.js';
import { api, ApiError } from '../shell/api.js';
import { Failure } from '../shell/EmptyState.js';
import { Link, useNavigate, type Surface } from '../shell/router.js';
import { sharedActivityTransport } from '../shell/stream.js';
import { useAsync } from '../shell/useAsync.js';
import { appRequestFor, countLine, standAloneExperiments, standings } from './order.js';

interface WorkspaceView {
  readonly apps: readonly AppResource[];
  readonly experiments: readonly ExperimentResource[];
  readonly designs: readonly StoreDesignResource[];
}

/**
 * The Workspace. One shared table of Apps, with the work in flight at the top. There
 * are no users, roles, owners or tenants here, and there is nothing to configure
 * between an App and its first Experiment.
 */
function Workspace(): ReactNode {
  const activity = useActivity(sharedActivityTransport);
  const loaded = useAsync<WorkspaceView>(async () => {
    const [apps, experiments, designs] = await Promise.all([
      api.listApps(),
      api.listExperiments(),
      api.listStoreDesigns(),
    ]);
    return {
      apps: apps.apps,
      experiments: standAloneExperiments(experiments.experiments, designs.designs),
      designs: designs.designs,
    };
  }, []);
  const { reload } = loaded;

  if (loaded.error !== null) {
    return (
      <section className="page">
        <p className="eyebrow">Workspace</p>
        <Failure message={loaded.error} onRetry={reload} />
      </section>
    );
  }

  if (loaded.value === null) return <section className="page" aria-busy="true" />;

  // An App in the archive is out of the shelf; the archive below is where it is brought back.
  const archived = loaded.value.apps.filter((app) => !isInWork(app));
  const ranked = standings(
    loaded.value.apps.filter(isInWork),
    loaded.value.experiments,
    activity.jobs.map((entry) => entry.job?.appId ?? null),
    loaded.value.designs,
  );

  if (ranked.length === 0) {
    return (
      <section className="page">
        <p className="eyebrow">Workspace</p>
        <h1>{archived.length === 0 ? 'Пока нет Apps.' : 'Все Apps в архиве.'}</h1>
        <p className="note">
          {archived.length === 0
            ? 'Добавьте первый App, чтобы начать Experiment по креативам его Store Listing в Google Play. Достаточно адреса Store Listing или имени пакета; одно название подойдёт для ещё не опубликованного.'
            : 'Верните нужный App из архива ниже или добавьте новый: достаточно адреса Store Listing или имени пакета.'}
        </p>
        <AddApp onAdded={reload} />
        <Link to="/apps/new" className="secondary-link">
          Добавить App вручную
        </Link>
        <ArchivedApps apps={archived} onRestored={reload} />
      </section>
    );
  }

  return (
    <section className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Workspace · сначала активные</p>
          <h1>Продолжите с того, где идёт работа.</h1>
          <p className="note">Новое оформление — иконка и скриншоты от общего задания до ZIP.</p>
          <Link to="/store-designs/new" className="primary-link">Создать новое оформление</Link>
        </div>
        <div>
          <AddApp onAdded={reload} />
          <Link to="/apps/new" className="secondary-link">
            Добавить App вручную
          </Link>
        </div>
      </div>
      <ul className="app-shelf" aria-label="Apps">
        {ranked.map((standing) => (
          <li key={standing.app.id} className="app-card">
            <Link to={`/apps/${standing.app.id}`} className="app-card-link">
              {standing.app.displayName}
            </Link>
            <p className="note">{countLine(standing)}</p>
            {standing.activeJobs > 0 ? (
              <p className="app-card-live">{`${standing.activeJobs} в работе`}</p>
            ) : null}
          </li>
        ))}
      </ul>
      <ArchivedApps apps={archived} onRestored={reload} />
    </section>
  );
}

/** The only way into the Workspace, and the only action an empty one offers. */
function AddApp({ onAdded }: { onAdded: () => void }): ReactNode {
  const [paste, setPaste] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError(null);
      try {
        const created = await api.createApp(appRequestFor(paste));
        setPaste('');
        onAdded();
        navigate(`/apps/${created.app.id}`);
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Не удалось добавить App.');
      } finally {
        setBusy(false);
      }
    },
    [navigate, onAdded, paste],
  );

  return (
    <form className="add-app" onSubmit={(event) => void submit(event)}>
      <label htmlFor="app-paste">Адрес Google Play, имя пакета или название</label>
      <input
        id="app-paste"
        name="paste"
        value={paste}
        onChange={(event) => setPaste(event.target.value)}
        placeholder="play.google.com/store/apps/details?id=…"
      />
      {error === null ? null : (
        <p className="failure" role="alert">
          {error}
        </p>
      )}
      <button className="primary" type="submit" disabled={busy || paste.trim() === ''}>
        {busy ? 'Добавляю…' : 'Добавить App'}
      </button>
    </form>
  );
}

export const surface: Surface = {
  id: 'workspace',
  path: '/',
  title: 'Workspace',
  render: () => <Workspace />,
};
